/**
 * pageVerb — the page → wall link, as pure functions (wall plan e, `thinking/plans/
 * 2026_08_31_wall_plan_e_toile_page_to_wall.md`): what lights the verb, what the press posts, where
 * it lands, and the token that carries a press across the sign-in card.
 *
 * It lives here rather than inside Toile's own stage component because plans f and g put a verb of
 * the same shape on the Narcis and Ovid pack pages — one token scheme, shared rather than
 * duplicated per page.
 *
 * The token's shape is amendment S4's: the page mints the `request_id` BEFORE the redirect and
 * carries it as `rid`, so the re-submit on return posts the SAME key the press would have posted.
 * A return that happens more than once — a Back, a bfcache restore, a second sign-in in another tab
 * — then lands on plan c's `(userId, requestId, position)` idempotency and yields the same nine
 * rows, never eighteen.
 *
 * The page re-submits, not the wall: at the moment the card opens the batch does not exist yet, so
 * there is nothing for the wall to be handed (plan e § Decided).
 */
import {
    AUTH_CALLBACK_KEYS,
    MAX_REQUEST_ID_CHARS,
    RETURN_TOKEN_KEYS,
    type ContinuePackRequest,
} from './contract'
import {clampDirection} from './ui/chains'

/**
 * The verb is unlit until there are words (BRIEF §9, board `PageVerb`: "typed words light Generate;
 * empty field = nothing to do"). A chip does not light it — a chip FIRES on tap, so by the time
 * anything is read the words are already in the field. Whitespace-only is empty, the same
 * `clampDirection` rule the wall's own field and `prompt.server.ts` apply.
 */
export function verbLit(field: string): boolean {
    return clampDirection(field) !== ''
}

/**
 * A chip's words into the field (operator ruling 2026-09-01, reversing "each chip fires" — BRIEF
 * § the edges): a chip is a suggestion, not a trigger. Empty field takes the chip alone; words
 * already there get the chip appended after `, `; the same chip pressed again — the field already
 * reads exactly it — changes nothing. Only the verb fires, so this never posts anything itself.
 */
export function withChip(field: string, chip: string): string {
    const trimmedField = field.trim()
    const trimmedChip = chip.trim()
    if (!trimmedField) return trimmedChip
    if (trimmedField === trimmedChip) return trimmedField
    return `${trimmedField}, ${trimmedChip}`
}

/** Whether a chip's words fired the press — `page_verb`'s and `page_generate`'s own `chip_used`
 *  (decision 4, plan `2026_09_21_wall_gaps.md`): a chip and typed words post the identical body, so
 *  this substring check is the only place the two differ. Single source for both events —
 *  `page_verb` fires on the press, `page_generate` on the write it leads to, and they must agree. */
export function chipUsed(direction: string, chips: readonly string[]): boolean {
    const lower = direction.toLowerCase()
    return chips.some((chip) => lower.includes(chip.toLowerCase()))
}

/** What the press posts (`POST /continue/pack`). The direction is clamped once, here, so the body
 *  and the token below can never disagree about what was asked for. */
export function packContinueBody(packSlug: string, direction: string, requestId: string): ContinuePackRequest {
    return {pack_slug: packSlug, direction: clampDirection(direction), request_id: requestId}
}

/** Where the wall opens after the press: the batch lifted to the top, no column (plan e § Decided). */
export function batchWallPath(batchId: string): string {
    return `/?batch=${encodeURIComponent(batchId)}`
}

/** Where a page CELL goes: the wall armed on that cell's tile, nothing fired — "a cell is a quick
 *  look: it lands armed" (BRIEF §9). The wall reads `basis` through the same return-token effect a
 *  sign-in return comes back on, so the two entries need no second reader. */
export function basisWallPath(tileId: number): string {
    return `/?basis=${tileId}`
}

export type PackReturn = {direction: string; requestId: string}

/**
 * The page's own URL after the sign-in card — `?pack=&direction=&rid=` on the pack's own path.
 * `pack` is carried even though the path already names it: it is what `readPackReturnToken` checks
 * the token against, so a token pasted onto (or surviving a redirect onto) another pack's page is
 * ignored rather than firing that pack's nine.
 */
export function packReturnToken({packSlug, direction, requestId}: {
    packSlug: string
    direction: string
    requestId: string
}): string {
    const params = new URLSearchParams()
    params.set('pack', packSlug)
    const clamped = clampDirection(direction)
    if (clamped) params.set('direction', clamped)
    params.set('rid', requestId)
    return `/${packSlug}?${params.toString()}`
}

/**
 * The token read back on the page, or `null` when there is nothing to act on: another pack's token,
 * one with no `rid` (nothing to be idempotent on — a re-submit without the minted key is exactly
 * how a return makes a second batch), or one whose direction is empty (the verb was never lit).
 */
export function readPackReturnToken(search: URLSearchParams, packSlug: string): PackReturn | null {
    if (search.get('pack') !== packSlug) return null
    const requestId = search.get('rid') ?? ''
    if (!requestId || requestId.length > MAX_REQUEST_ID_CHARS) return null
    const direction = clampDirection(search.get('direction') ?? '')
    if (!direction) return null
    return {direction, requestId}
}

/** The URL's params with every token and auth-callback key removed — read once, then stripped (S4),
 *  whatever the params turned out to say. Shared with the wall, which strips the same set. */
export function strippedSearch(search: URLSearchParams): URLSearchParams {
    const next = new URLSearchParams(search)
    for (const key of RETURN_TOKEN_KEYS) next.delete(key)
    for (const key of AUTH_CALLBACK_KEYS) next.delete(key)
    return next
}
